import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useRestApi from 'hooks/useRestApi';
import { regionalApiUrl } from 'utils/apiUrl';
import { HierarchyTenant, TenantHierarchyResponse } from 'App.types';
import { Typography } from '@bb-ui/react-library';
import { useJobRegion } from 'contexts/JobRegionContext';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { useTenantContext } from 'contexts/TenantContext';
import {
  DataExportStatusMap,
  ResourceRegionMap,
  useLearnDataExport,
} from 'hooks/useLearnDataExport';

export interface DataSharingContextProps {
  loading: boolean;
  error?: Error;
  fetchHierarchyData: () => Promise<void>;
  hierarchyTenants?: HierarchyTenant[];
  selectedLinkedTenants?: string[];
  setSelectedLinkedTenants?: (tenants: string[]) => void;
  initialLinkedTenants?: string[];
  initialReportingTenant?: string;
  /** Region of the Illuminate tenant that requests the data sharing */
  requestingRegion?: string;
  /** Whether a Learn tenant has a LearnDms tenant (data export) below it */
  learnDataExport?: DataExportStatusMap;
  /** Where the resources of a Learn tenant live, compared with the requesting region */
  learnResourceRegions?: ResourceRegionMap;
}

export interface DataSharingProviderProps {
  jobId: string;
  tenantId: string;
  children: React.ReactNode;
}

export const DataSharingContext = React.createContext<DataSharingContextProps>({
  loading: false,
  fetchHierarchyData: async () => {},
});

export const useDataSharingContext = () => React.useContext(DataSharingContext);

export const DataSharingProvider: React.FC<DataSharingProviderProps> = ({
  children,
  jobId,
  tenantId,
}) => {
  const { t } = useTranslation();
  const [hierarchyTenants, setHierarchyTenants] = useState<HierarchyTenant[]>([]);
  const [selectedLinkedTenants, setSelectedLinkedTenants] = useState<string[]>([]);
  const [initialLinkedTenants, setInitialLinkedTenants] = useState<string[]>([]);
  const [initialReportingTenant, setInitialReportingTenant] = useState<string | undefined>();
  const [requestingRegion, setRequestingRegion] = useState<string | undefined>();
  const [allHierarchyTenants, setAllHierarchyTenants] = useState<HierarchyTenant[]>();
  const hasFetchedRef = useRef(false);

  const { jobRegion, showRegionPicker } = useJobRegion();
  const { tenant } = useTenantContext();
  const { stage } = useAppConfigContext();
  const { dataExport: learnDataExport, resourceRegions: learnResourceRegions } =
    useLearnDataExport(allHierarchyTenants);

  const { error, loading, fetch } = useRestApi<TenantHierarchyResponse>('', {
    manual: true,
  });

  const fetchHierarchyData = React.useCallback(async () => {
    // Prevent refetching
    if (hasFetchedRef.current) {
      return;
    }

    // Mark as fetched
    hasFetchedRef.current = true;

    const basePath =
      jobId === 'EditTenantSharing'
        ? `tenants/illuminate/${tenantId}/hierarchy`
        : `tenants/${tenantId}/hierarchy`;

    const currentRegion =
      (showRegionPicker && jobRegion ? jobRegion : tenant?.region) ?? 'us-east-1';
    setRequestingRegion(currentRegion);

    const hierarchyApiEndpoint = regionalApiUrl({
      capabilityName: 'data',
      region: currentRegion,
      apiPath: basePath,
      stage,
    });

    try {
      const { data } = await fetch({
        url: hierarchyApiEndpoint,
      });

      if (!data) return;

      // Allow only learn, ally or null tenant types.
      // Tenants from other regions stay in the list, the picker shows a warning for them.
      const allowedTypes = ['learn', 'ally'];
      const filteredTenants = data.tenants.filter(
        (t: HierarchyTenant) =>
          t.id !== tenantId && // remove current tenant
          (t.tenantType === null || allowedTypes.includes(t.tenantType?.toLowerCase() ?? '')),
      );
      setHierarchyTenants(filteredTenants);
      setAllHierarchyTenants(data.tenants);

      // Extract initially selected tenants from API response
      const linkedTenants = filteredTenants
        .filter((t: HierarchyTenant) => t.dataSharingInd === true)
        .map((t: HierarchyTenant) => t.id);

      const reportingTenant = filteredTenants.find(
        (t: HierarchyTenant) => t.usedInReportingInd === true,
      );

      setInitialLinkedTenants(linkedTenants);
      setInitialReportingTenant(reportingTenant?.id);
    } catch {
      // Reset flag on error so it can be retried
      hasFetchedRef.current = false;
      //errors are handled by the useRestApi hook, we can ignore them here, we just need to catch them
    }
  }, [fetch, jobId, tenantId, showRegionPicker, jobRegion, tenant?.region, stage]);

  const contextValue: DataSharingContextProps = React.useMemo(
    () => ({
      loading,
      error,
      fetchHierarchyData,
      hierarchyTenants,
      selectedLinkedTenants,
      setSelectedLinkedTenants,
      initialLinkedTenants,
      initialReportingTenant,
      requestingRegion,
      learnDataExport,
      learnResourceRegions,
    }),
    [
      loading,
      error,
      fetchHierarchyData,
      hierarchyTenants,
      selectedLinkedTenants,
      initialLinkedTenants,
      initialReportingTenant,
      requestingRegion,
      learnDataExport,
      learnResourceRegions,
    ],
  );

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {t('dataSharing.loadError')}
      </Typography>
    );
  }

  return <DataSharingContext.Provider value={contextValue}>{children}</DataSharingContext.Provider>;
};
